import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getManageStream, updateManageStream, type StreamEmbed } from "../lib/api";
import { ManageClubNav, useManagedClub } from "../components/ManageClubNav";
import { Trash3FillIcon } from "../components/icons";
import Toast from "../components/Toast";

const MAX_EMBEDS = 6; // matches backend lib/clubContent.ts

/** /manage/:clubId/stream -- the club's LiveRC page (drives the red "live"
 *  camera icon in the nav) and the players embedded on its /live page. */
export default function ManageStreamPage() {
  const { clubId = "" } = useParams();
  const { club, error: clubError } = useManagedClub(clubId);
  const [liveRcUrl, setLiveRcUrl] = useState("");
  const [embeds, setEmbeds] = useState<StreamEmbed[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getManageStream(clubId)
      .then((res) => {
        setLiveRcUrl(res.stream.liveRcUrl ?? "");
        setEmbeds(res.stream.embeds);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load stream settings."));
  }, [clubId]);

  function setEmbed(i: number, patch: Partial<StreamEmbed>) {
    setEmbeds((prev) => prev && prev.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!embeds) return;
    setError(null);
    setSaving(true);
    try {
      const res = await updateManageStream(clubId, { liveRcUrl, embeds });
      setLiveRcUrl(res.stream.liveRcUrl ?? "");
      setEmbeds(res.stream.embeds);
      setInfo("Stream settings saved. The club's site picks them up within a minute.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  if (clubError) return <p className="err">{clubError}</p>;
  if (!club || !embeds) return error ? <p className="err">{error}</p> : <p>Loading…</p>;

  return (
    <div>
      <ManageClubNav club={club} />
      <h1>{club.name} streaming</h1>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleSave} className="card">
        <label htmlFor="stream-liverc">LiveRC page</label>
        <input
          id="stream-liverc"
          type="url"
          placeholder="https://yourtrack.liverc.com/live/"
          value={liveRcUrl}
          onChange={(e) => setLiveRcUrl(e.target.value)}
        />
        <p className="muted" style={{ fontSize: 13 }}>
          The camera icon in the nav turns red while LiveRC says this track is streaming. Leave blank if you don't use LiveRC.
        </p>

        <h3 style={{ marginTop: "1rem" }}>Embedded players</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          Shown on your <Link to="/live">Live</Link> page, in this order. Paste a YouTube link (it's turned into the
          player automatically) or any https embed URL your streaming service gives you.
        </p>
        {embeds.map((e, i) => (
          <div className="row" key={i} style={{ marginTop: "0.5rem" }}>
            <div style={{ flex: "0 1 180px" }}>
              <label htmlFor={`embed-label-${i}`}>Label</label>
              <input id={`embed-label-${i}`} placeholder="Main straight" value={e.label} onChange={(ev) => setEmbed(i, { label: ev.target.value })} />
            </div>
            <div style={{ flex: "1 1 260px" }}>
              <label htmlFor={`embed-url-${i}`}>Embed URL</label>
              <input id={`embed-url-${i}`} type="url" placeholder="https://" value={e.url} onChange={(ev) => setEmbed(i, { url: ev.target.value })} required />
            </div>
            <button
              type="button"
              className="link"
              style={{ flex: "0 0 auto" }}
              title="Remove"
              aria-label={`Remove ${e.label || `stream ${i + 1}`}`}
              onClick={() => setEmbeds(embeds.filter((_, j) => j !== i))}
            >
              <Trash3FillIcon />
            </button>
          </div>
        ))}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button
            type="button"
            className="secondary"
            disabled={embeds.length >= MAX_EMBEDS}
            onClick={() => setEmbeds([...embeds, { label: "", url: "" }])}
          >
            Add a player
          </button>
          <button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
